import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { Icon } from "../components/icons.js";

type Tone = "neutral" | "green" | "red";
interface Toast {
  id: number;
  text: string;
  tone: Tone;
}

interface ToastApi {
  show: (text: string, tone?: Tone) => void;
  success: (text: string) => void;
  error: (text: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const show = useCallback((text: string, tone: Tone = "neutral") => {
    const id = ++counter;
    setItems((xs) => [...xs, { id, text, tone }]);
    setTimeout(() => setItems((xs) => xs.filter((t) => t.id !== id)), tone === "red" ? 7000 : 4000);
  }, []);
  const api = useMemo<ToastApi>(
    () => ({ show, success: (t) => show(t, "green"), error: (t) => show(t, "red") }),
    [show],
  );
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            <Icon name={t.tone === "red" ? "warning" : t.tone === "green" ? "check" : "info"} />
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast outside ToastProvider");
  return ctx;
}
